import { keyBy } from "lodash";

import { formatAmount } from "utilities/dist/helpers";
import { ProductTypeAcronyms, ProductTypes } from "../constants";
import { ProductLinkRenderer } from "./cell-renderers.component";

export const allGridColumns = keyBy(
    [
        {
            minWidth: 260,
            field: "id",
            headerName: "ID",
            hide: true,
        },
        {
            minWidth: 260,
            field: "product",
            headerName: "Name",
            filter: "agTextColumnFilter",
            cellRenderer: ProductLinkRenderer,
            valueGetter: function ({ data }) {
                return !data ? null : data.product;
            },
        },
        {
            hide: true,
            minWidth: 60,
            field: "internal_sku",
            headerName: "SKU",
        },
        {
            hide: true,
            field: "upc",
            headerName: "UPC",
        },
        {
            minWidth: 25,
            headerName: "Type",
            field: "product_type",
            filter: "agTextColumnFilter",
            valueGetter: function ({ data }) {
                const product_type = data
                    ? Object.keys(ProductTypes).find(
                          key => ProductTypes[key] === data.product_type.toUpperCase()
                      )
                    : undefined;
                return data ? ProductTypeAcronyms[product_type] : "";
            },
        },
        {
            minWidth: 25,
            headerName: "Size",
            field: "product_size",
            filter: "agTextColumnFilter",
        },
        {
            minWidth: 40,
            type: "numericColumn",
            headerName: "Purchased",
            field: "last_purchase_price",
            filter: "agNumberColumnFilter",
            valueFormatter: ({ data, colDef }) => formatAmount(colDef.valueGetter({ data })),
            valueGetter: function ({ data }) {
                if (!data || !data.last_purchase_price) return null;
                return data.last_purchase_price;
            },
        },
        {
            minWidth: 20,
            type: "numericColumn",
            field: "product_price",
            headerName: "List Price",
            filter: "agNumberColumnFilter",
            valueFormatter: ({ data, colDef, context }) =>
                formatAmount(colDef.valueGetter({ data, context })),
            valueGetter: function ({ data, context }) {
                let product_price = null;
                if (!data || !data?.product_price) return null;
                if (!data.priceChanges?.length) {
                    product_price = data.product_price;
                }
                const latestPriceChange = data.priceChanges
                    .filter(priceChange => Date.parse(priceChange.effective_date) <= new Date())
                    .filter(
                        priceChange =>
                            priceChange.customer_id === context.customer_id ||
                            priceChange.customer_id === null
                    )
                    .sort(
                        (a, b) => Date.parse(b.effective_date) - Date.parse(a.effective_date)
                    )?.[0];
                if (latestPriceChange?.new_price) {
                    product_price = latestPriceChange.new_price;
                } else {
                    product_price = data.product_price;
                }
                return product_price;
            },
        },
        {
            minWidth: 25,
            field: "units_sold",
            type: "numericColumn",
            headerName: "Units Sold",
            filter: "agNumberColumnFilter",
        },
        {
            minWidth: 25,
            field: "amount_sold",
            type: "numericColumn",
            headerName: "Total $",
            filter: "agTextColumnFilter",
            valueFormatter: ({ data, colDef }) => formatAmount(colDef.valueGetter({ data })),
            valueGetter: function ({ data }) {
                if (!data) return null;
                return data.amount_sold;
            },
        },
        {
            minWidth: 20,
            type: "numericColumn",
            field: "profit_amount",
            headerName: "Profit $",
            filter: "agTextColumnFilter",
            valueFormatter: ({ data, colDef }) => formatAmount(colDef.valueGetter({ data })),
            valueGetter: function ({ data }) {
                if (!data || !data?.last_purchase_price) return null;
                if (data.units_sold === 0) return null;
                return data.amount_sold - data.units_sold * data.last_purchase_price;
            },
        },
        {
            minWidth: 20,
            type: "numericColumn",
            field: "profit_percentage",
            headerName: "Profit %",
            filter: "agTextColumnFilter",
            valueFormatter: ({ data }) => data?.profit_percentage?.toString().concat(" %"),
            valueGetter: ({ data }) => data?.profit_percentage,
        },
        {
            hide: true,
            minWidth: 60,
            field: "distributor",
            headerName: "Distributor",
            filter: false,
            sortable: false,
            valueGetter: ({ data }) => data?.inventory?.distributor || null,
        },
        {
            hide: true,
            minWidth: 0,
            field: "archived",
            type: "numericColumn",
            headerName: "Archived",
            filter: "agNumberColumnFilter",
        },
    ],
    "field"
);
